import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Container, Tab, Tabs } from "@mui/material";
import { useGetOrgByIdQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useGetUsersByOrgAndStatusQuery, useGetBudgetRequestsQuery } from "state/rtkQuery/user/userQueries";

import AdminDashboard from "../../AdminDashboard";
import UlbRequestsTable, { UlbFilterChips, UlbRequestFilters } from "../UlbRequests/UlbRequestsTable";
import RefreshButton from "components/shared/RefreshButton/RefreshButton";
import { buildDefaultFilters } from "../UlbRequests/ulbFilters";

const ACCENT = "#26890d";

// Both tabs are "requests waiting on an Owner/Approver" — org join requests
// (existing AdminDashboard table, untouched) and AIC budget change requests
// (UlbRequestsTable) — grouped under one route/page instead of two, with the
// tab strip replacing the old static "Pending Approvals" label. The ULB tab
// mirrors AdminDashboard's own header markup (org name + search box) exactly.
// How long the lists wait before reloading themselves when nobody has pressed Refresh.
const AUTO_REFRESH_MS = 5 * 60 * 1000;

const PendingApprovalsTabs = () => {
  const { orgId } = useParams();
  const { data: orgData } = useGetOrgByIdQuery(orgId);
  const { data: platformData } = useGetPlatformsQuery();
  const platformId = platformData?._id;
  const [tab, setTab] = useState(0);
  const [ulbFilters, setUlbFilters] = useState(buildDefaultFilters);

  // Dedicated, lightweight count-only queries (perPage: 1, only `.total` is
  // read) — independent of whichever tab's actual content is mounted below,
  // so both tab labels get a count immediately on load without needing to
  // keep both tabs' heavy components (and their DataGrids) alive at once.
  // Deliberately NOT reusing AdminDashboard's/UlbRequestsTable's own queries:
  // an earlier version kept both components always-mounted to share their
  // queries, but that made whichever tab was hidden via display:none report
  // a 0px DataGrid height (MUI warns/mis-sizes on unhide) — plain, separate
  // queries here avoid that entirely.
  const {
    data: pendingApprovalsCountData,
    refetch: refetchPendingApprovalsCount,
    isFetching: isPendingApprovalsCountFetching,
  } = useGetUsersByOrgAndStatusQuery(
    { orgId, status: "Pending", platformId, page: 1, perPage: 1 },
    { skip: !orgId || !platformId, refetchOnMountOrArgChange: true }
  );
  const {
    data: ulbPendingCountData,
    refetch: refetchUlbPendingCount,
    isFetching: isUlbPendingCountFetching,
  } = useGetBudgetRequestsQuery(
    { orgId, platformId, action: "all", status: "pending", page: 1, perPage: 1 },
    { skip: !orgId || !platformId, refetchOnMountOrArgChange: true }
  );
  const pendingApprovalsCount = pendingApprovalsCountData?.total ?? null;
  const ulbPendingCount = ulbPendingCountData?.total ?? null;

  // Reload the open tab's list and both tab counts. The tables listen for refreshTick and refetch.
  const [refreshTick, setRefreshTick] = useState(0);
  const lastRefreshRef = useRef(Date.now());
  const refreshAll = () => {
    lastRefreshRef.current = Date.now();
    refetchPendingApprovalsCount();
    refetchUlbPendingCount();
    setRefreshTick((tick) => tick + 1);
  };

  // Reload on its own every 5 minutes, counted from the last refresh — pressing the button restarts
  // the clock, so it only fires when you haven't refreshed. Skipped while the tab is hidden; coming
  // back after being away longer than that refreshes straight away.
  const refreshAllRef = useRef(refreshAll);
  refreshAllRef.current = refreshAll;
  useEffect(() => {
    const refreshIfStale = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastRefreshRef.current >= AUTO_REFRESH_MS) refreshAllRef.current();
    };
    const timer = setInterval(refreshIfStale, 30 * 1000);
    document.addEventListener("visibilitychange", refreshIfStale);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshIfStale);
    };
  }, []);
  const refreshButton = (
    <RefreshButton
      label={tab === 1 ? "Refresh budget requests" : "Refresh pending approvals"}
      isRefreshing={isPendingApprovalsCountFetching || isUlbPendingCountFetching}
      onClick={refreshAll}
    />
  );

  const tabLabel = (label, count) => (count == null ? label : `${label} (${count})`);

  const tabsSlot = (
    <Tabs
      value={tab}
      onChange={(e, v) => setTab(v)}
      sx={{
        minHeight: 36,
        mb: 1,
        ml: 0,
        "& .MuiTab-root": {
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.9rem",
          minHeight: 36,
          padding: "0 !important",
          minWidth: "0 !important",
          marginRight: "1.5rem",
        },
        "& .Mui-selected": { color: `${ACCENT} !important` },
        "& .MuiTabs-indicator": { backgroundColor: ACCENT },
      }}
    >
      <Tab label={tabLabel("Pending Approvals", pendingApprovalsCount)} />
      <Tab label={tabLabel("AIC Budget Requests", ulbPendingCount)} />
    </Tabs>
  );

  if (tab === 1) {
    return (
      <Container sx={{ maxWidth: "1700px !important", flex: "1", marginTop: "3rem" }}>
        <Box sx={{ height: "calc(100vh - 240px)", minHeight: 600 }}>
          <div className="admin-headerSearchFlex">
            <div className="admin-dashboard-header">
              <span className="admin-dashboard-header-content">
                <strong>{orgData?.name}</strong>
              </span>
            </div>
            <div className="admin-dashboard-search">
              {refreshButton}
              <UlbRequestFilters filters={ulbFilters} onFiltersChange={setUlbFilters} />
            </div>
          </div>
          {tabsSlot}
          <UlbFilterChips filters={ulbFilters} onFiltersChange={setUlbFilters} />
          <UlbRequestsTable filters={ulbFilters} onFiltersChange={setUlbFilters} refreshTick={refreshTick} />
          <div
            className="admin-dashboard-support-container"
            style={{ marginTop: "30px", padding: "10px 0px", paddingBottom: "2rem" }}
          >
            <p>
              Please fill our{" "}
              <a
                href="https://deloitteglobal.service-now.com/sp?id=sc_cat_item&sys_id=4a417b4f1b17855cbcae55b0604bcb1c"
                target="_blank"
                className="request-support-link"
                rel="noreferrer"
              >
                Support Request
              </a>
              &nbsp; Form if you face any issues.
            </p>
          </div>
        </Box>
      </Container>
    );
  }

  return <AdminDashboard tabsSlot={tabsSlot} headerActions={refreshButton} refreshTick={refreshTick} />;
};

export default PendingApprovalsTabs;
